
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface CreateBrandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateBrand: (websiteUrl: string) => Promise<any>;
}

export const CreateBrandDialog = ({ 
  open, 
  onOpenChange, 
  onCreateBrand 
}: CreateBrandDialogProps) => {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateBrand = async () => {
    if (!websiteUrl.trim()) return;
    
    setIsCreating(true);
    try {
      await onCreateBrand(websiteUrl);
      setWebsiteUrl("");
      onOpenChange(false);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && websiteUrl.trim()) {
      handleCreateBrand();
    }
  };

  const formatUrl = (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWebsiteUrl(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitleWithBug
            popupName="Create New Brand"
            popupContext="/dashboard"
            isOpen={open}
          >
            Create New Brand
          </DialogTitleWithBug>
          <DialogDescription>
            Enter your website URL to create a new brand
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            placeholder="https://www.example.com"
            value={websiteUrl}
            onChange={handleUrlChange}
            onKeyPress={handleKeyPress}
            className="w-full"
            disabled={isCreating}
            type="url"
          />
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateBrand}
            disabled={!websiteUrl.trim() || isCreating}
          >
            {isCreating ? "Analyzing website..." : "Create Brand"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
